import { useEffect, useMemo, useRef, useState } from "react";
import { CONTACT_INFO } from "../config/constants";

const QUICK_REPLIES = [
  "Services",
  "Pricing",
  "Start a project",
  "Founder",
  "Contact info",
  "Business hours",
  "Privacy & cookies",
];

const normalizeText = (value) => value.toLowerCase().trim();

const buildResponse = (text) => ({
  id: crypto.randomUUID(),
  role: "bot",
  content: text,
});

const buildRichResponse = (content) => ({
  id: crypto.randomUUID(),
  role: "bot",
  content,
});

const getBotResponse = (input) => {
  const text = normalizeText(input);

  if (/(hi|hello|hey|good morning|good evening)/.test(text)) {
    return buildResponse("Hi! I'm the ZS NEXATECH assistant. How can I help you today?");
  }

  if (/(service|offer|what do you do|capabilit|stack)/.test(text)) {
    return buildResponse(
      "We build modern websites, web apps, mobile apps, UI/UX, and custom software. Tell me your goal and timeline and I'll guide you."
    );
  }

  if (/(price|pricing|cost|budget|quote|estimate)/.test(text)) {
    return buildResponse(
      "Project scope varies by requirements and timeline. Share your goals and we'll send a detailed proposal within 24 hours."
    );
  }

  if (/(portfolio|work|projects|case study)/.test(text)) {
    return buildResponse(
      "You can view recent work on the Portfolio page. If you want a similar solution, share your idea and we'll map the scope."
    );
  }

  if (/(founder|owner|ceo|lead developer|leadership)/.test(text)) {
    return buildRichResponse(
      <span>
        Founder: <strong>Tarannum Mushtaq Ahmed</strong> - Founder & Lead Full Stack Developer.
        Full-stack developer dedicated to transforming ideas into impactful digital solutions.
        {" "}
        <a href="/founder" className="text-amber-300 underline underline-offset-4">
          View full profile
        </a>
        .
      </span>
    );
  }

  if (/(contact|email|mail|phone|call|whatsapp|inquiry|enquiry|support)/.test(text)) {
    return buildRichResponse(
      <span>
        You can reach us at <strong>{CONTACT_INFO.email}</strong> or WhatsApp <strong>{CONTACT_INFO.phone}</strong>.
        {" "}
        <a href={CONTACT_INFO.whatsappUrl} target="_blank" rel="noreferrer" className="text-amber-300 underline underline-offset-4 whatsapp-link">
          Chat on WhatsApp
        </a>
        .
      </span>
    );
  }

  if (/(hour|open|timing|availability|available|schedule)/.test(text)) {
    return buildResponse("Business hours: Monday-Saturday 10:00 AM-7:00 PM IST. Sundays are email/WhatsApp only.");
  }

  if (/(cookie|privacy|policy)/.test(text)) {
    return buildRichResponse(
      <span>
        You can review our <a href="/privacy-policy" className="text-amber-300 underline underline-offset-4">Privacy Policy</a>
        {" "}and <a href="/cookie-policy" className="text-amber-300 underline underline-offset-4">Cookie Policy</a> anytime.
      </span>
    );
  }

  if (/(start|project|get started|kickoff|proposal)/.test(text)) {
    return buildResponse(
      "Great! Share your goals, features, and timeline. We'll respond with a tailored plan within 24 hours."
    );
  }

  return buildResponse(
    "Thanks! I can help with services, pricing, timelines, or contact details. What would you like to know?"
  );
};

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    buildResponse("Hi! I'm the ZS NEXATECH assistant. Ask me about services, pricing, or how to contact us."),
  ]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const quickReplies = useMemo(() => QUICK_REPLIES, []);

  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === "#help-desk") {
        setIsOpen(true);
      }
    };

    openFromHash();
    window.addEventListener("hashchange", openFromHash);

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [messages]);

  const handleSend = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    const userMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMessage, getBotResponse(trimmed)]);
    setInput("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSend(input);
  };

  return (
    <div id="help-desk" className="fixed bottom-6 right-6 z-40 pointer-events-none">
      <div className={`transition-all duration-300 ${isOpen ? "opacity-100 translate-y-0 z-50 pointer-events-auto" : "opacity-0 translate-y-4 z-40 pointer-events-none"}`}>
        <div className={`w-85 max-w-[90vw] rounded-2xl border border-white/20 bg-slate-900/90 backdrop-blur-xl shadow-2xl overflow-hidden ${isOpen ? "pointer-events-auto" : "pointer-events-none"}`}>
          <div className="flex items-center justify-between px-4 py-3 bg-linear-to-r from-amber-500/30 via-orange-500/20 to-purple-500/20 border-b border-white/10">
            <div>
              <p className="text-sm font-semibold text-red-700">ZS Help Desk</p>
              <p className="text-xs text-white/70">Replies instantly with key info</p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition"
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="px-4 py-3 space-y-3 max-h-80 overflow-y-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`rounded-2xl px-3 py-2 text-sm leading-relaxed max-w-[85%] ${
                    message.role === "user"
                      ? "bg-amber-500/80 text-slate-900"
                      : "bg-white/10 text-white"
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="px-4 pb-3">
            <div className="grid grid-cols-3 gap-2 mb-3">
              {quickReplies.map((reply) => (
                <button
                  key={reply}
                  type="button"
                  onClick={() => handleSend(reply)}
                  className="px-2 py-2 rounded-lg border border-amber-300/50 text-xs text-amber-100 font-medium bg-linear-to-br from-amber-500/10 to-orange-500/10 hover:from-amber-500/25 hover:to-orange-500/20 hover:border-amber-300/80 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/30"
                >
                  {reply}
                </button>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ask a question..."
                className="flex-1 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400"
                aria-label="Chat message"
              />
              <button
                type="submit"
                className="px-4 py-2 rounded-full bg-amber-400 text-slate-900 text-sm font-semibold hover:bg-amber-300 transition"
              >
                Send
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float-rotate {
          0% {
            transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg);
          }
          25% {
            transform: rotateX(15deg) rotateY(15deg) rotateZ(-5deg);
          }
          50% {
            transform: rotateX(0deg) rotateY(25deg) rotateZ(0deg);
          }
          75% {
            transform: rotateX(-15deg) rotateY(15deg) rotateZ(5deg);
          }
          100% {
            transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg);
          }
        }

        @keyframes glow-pulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(249, 115, 22, 0.5), 0 0 40px rgba(249, 115, 22, 0.2);
          }
          50% {
            box-shadow: 0 0 40px rgba(249, 115, 22, 0.8), 0 0 80px rgba(249, 115, 22, 0.4);
          }
        }

        .help-desk-btn {
          animation: float-rotate 4s ease-in-out infinite, glow-pulse 3s ease-in-out infinite;
          transform-style: preserve-3d;
        }
      `}</style>

      <div className="group relative z-40 pointer-events-auto">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="help-desk-btn mt-3 ml-auto flex items-center justify-center w-20 h-20 rounded-full bg-linear-to-br from-orange-500 via-orange-400 to-red-600 text-white shadow-2xl hover:shadow-orange-500/60 transition-all duration-300 pointer-events-auto z-40"
          aria-label="Toggle AI Help Desk"
        >
          <svg
            className="w-10 h-10 drop-shadow-lg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Futuristic Tech Support Icon - 3D Robot Head */}
            {/* Head base */}
            <rect x="4" y="2" width="16" height="18" rx="2" />
            {/* Left eye - LED style */}
            <circle cx="8" cy="8" r="2" fill="currentColor" />
            {/* Right eye - LED style */}
            <circle cx="16" cy="8" r="2" fill="currentColor" />
            {/* Antenna */}
            <line x1="12" y1="2" x2="12" y2="0" strokeWidth="1.5" />
            {/* Tech circuit lines */}
            <path d="M6 12h3M15 12h3" strokeWidth="1.5" />
            {/* Mouth - Communication indicator */}
            <path d="M8 16h8" strokeWidth="2" />
            {/* Bottom tech accent */}
            <path d="M4 20h16" strokeWidth="1.5" />
            {/* Side panels for 3D effect */}
            <path d="M4 4v14M20 4v14" strokeWidth="1" opacity="0.6" />
          </svg>
        </button>

        {/* Tooltip - AI Help Desk */}
        <div className="absolute bottom-full right-0 mb-4 px-4 py-2 bg-linear-to-r from-orange-600 to-red-600 text-white text-sm font-bold rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none shadow-lg z-40">
          AI Help Desk
          <div className="absolute bottom-0 right-6 w-2 h-2 bg-red-600 rotate-45 translate-y-1"></div>
        </div>
      </div>
    </div>
  );
}
